import { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert,
  Button,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  TextField,
} from "@mui/material";
import { Form, Formik } from "formik";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import {
  CuratedPlaceOfSafetySchema,
  type AdHocPlaceOfSafetyUnbranded,
  type CuratedPlaceOfSafety,
  type CuratedPlaceOfSafetyUnbranded,
} from "@thalamos/common";
import { tk, type PersonName } from "@thalamos/vision-common";

import { PopupDialog } from "../../components/Dialog/PopupDialog.js";
import { PlaceOfSafetyPicker } from "../../components/FormBuilder/PlaceOfSafetyPicker/PlaceOfSafetyPicker.js";
import { useUser } from "../../Context/UserContext.js";
import {
  isQuickCreateEnabled,
  isTypingInto,
} from "../../hooks/quick_create.js";
import { runQuickCreate136 } from "../../hooks/quickCreate136.js";
import { createRoute } from "../../routing/createRoute.js";

type PickerValue =
  | CuratedPlaceOfSafetyUnbranded
  | AdHocPlaceOfSafetyUnbranded
  | ""
  | null
  | undefined;

interface QuickCreateFormValues {
  forename: string;
  surname: string;
  placeOfSafety: PickerValue;
}

const initialValues: QuickCreateFormValues = {
  forename: "",
  surname: "",
  placeOfSafety: "",
};

export type QuickCreateDemoIncidentDialogProps = {
  open: boolean;
  onClose: () => void;
  /** Rejects with the reason the incident could not be created. */
  onCreate: (args: {
    name: PersonName;
    curatedPlaceOfSafety: CuratedPlaceOfSafety;
  }) => Promise<void>;
};

export function QuickCreateDemoIncidentDialog({
  open,
  onClose,
  onCreate,
}: QuickCreateDemoIncidentDialogProps) {
  const { t } = useTranslation();

  const [isRunning, setIsRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // State lags a render behind, so the ref is what stops a second submit
  // starting a second run.
  const isRunningRef = useRef(false);

  const close = useCallback(() => {
    if (isRunningRef.current) {
      return;
    }
    onClose();
  }, [onClose]);

  const handleSubmit = async (values: QuickCreateFormValues) => {
    if (isRunningRef.current) {
      return;
    }
    isRunningRef.current = true;
    setIsRunning(true);
    setError(null);

    try {
      await onCreate({
        name: {
          forename: values.forename.trim(),
          surname: values.surname.trim(),
        },
        curatedPlaceOfSafety: CuratedPlaceOfSafetySchema.parse(
          values.placeOfSafety,
        ),
      });
      onClose();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : String(caught));
    } finally {
      isRunningRef.current = false;
      setIsRunning(false);
    }
  };

  return (
    <PopupDialog
      open={open}
      onClose={close}
      maxWidth="sm"
      testId="quick-create-demo-incident-dialog"
    >
      <DialogTitle sx={{ fontWeight: "bold" }}>
        {t(tk("quickCreateDemoIncident.title"))}
      </DialogTitle>
      <Formik<QuickCreateFormValues>
        initialValues={initialValues}
        onSubmit={handleSubmit}
      >
        {({ values, handleChange }) => {
          const hasPlaceOfSafety = CuratedPlaceOfSafetySchema.safeParse(
            values.placeOfSafety,
          ).success;
          const canSubmit =
            values.forename.trim() !== "" &&
            values.surname.trim() !== "" &&
            hasPlaceOfSafety;

          return (
            <Form>
              <DialogContent>
                <Stack spacing={3}>
                  <DialogContentText>
                    {t(tk("quickCreateDemoIncident.description"))}
                  </DialogContentText>

                  <TextField
                    label={t(tk("quickCreateDemoIncident.forenameLabel"))}
                    name="forename"
                    value={values.forename}
                    onChange={handleChange}
                    disabled={isRunning}
                    fullWidth
                    required
                    autoFocus
                    data-testid="quick-create-forename-input"
                  />
                  <TextField
                    label={t(tk("quickCreateDemoIncident.surnameLabel"))}
                    name="surname"
                    value={values.surname}
                    onChange={handleChange}
                    disabled={isRunning}
                    fullWidth
                    required
                    data-testid="quick-create-surname-input"
                  />

                  <PlaceOfSafetyPicker
                    fieldName="placeOfSafety"
                    label={{
                      translationKey: tk(
                        "quickCreateDemoIncident.placeOfSafetyLabel",
                      ),
                    }}
                    disableManualEntry
                  />

                  {error !== null && (
                    <Alert severity="error">
                      {t(tk("quickCreateDemoIncident.failed"))}: {error}
                    </Alert>
                  )}
                </Stack>
              </DialogContent>
              <DialogActions
                sx={{ px: 3, pb: 2, justifyContent: "space-between" }}
              >
                <Button
                  onClick={close}
                  color="error"
                  variant="outlined"
                  disabled={isRunning}
                >
                  {t(tk("quickCreateDemoIncident.cancel"))}
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={!canSubmit || isRunning}
                  data-testid="quick-create-demo-incident-submit"
                >
                  {isRunning
                    ? t(tk("quickCreateDemoIncident.creating"))
                    : t(tk("quickCreateDemoIncident.create"))}
                </Button>
              </DialogActions>
            </Form>
          );
        }}
      </Formik>
    </PopupDialog>
  );
}

/**
 * Shortcut (Option/Alt + Shift + D) that builds the same 136 incident as
 * `useQuickCreate136`, but for the person and place of safety a demo needs
 * rather than a randomly fabricated pair.
 *
 * Only active in dev/staging environments - renders nothing and binds no
 * shortcut in production.
 */
export function QuickCreateDemoIncident() {
  const navigate = useNavigate();
  const { user } = useUser();
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (!isQuickCreateEnabled) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      // Matched on code rather than key: the character Option+Shift+D produces
      // varies by keyboard layout, the physical key does not.
      const isChord =
        event.code === "KeyD" &&
        event.altKey &&
        event.shiftKey &&
        !event.ctrlKey &&
        !event.metaKey;

      if (!isChord || isTypingInto(document.activeElement)) {
        return;
      }

      event.preventDefault();
      setIsOpen(true);
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  const close = useCallback(() => {
    setIsOpen(false);
  }, []);

  const create = useCallback(
    async ({
      name,
      curatedPlaceOfSafety,
    }: {
      name: PersonName;
      curatedPlaceOfSafety: CuratedPlaceOfSafety;
    }) => {
      if (!user) {
        throw new Error("No user available");
      }
      const incidentWorkItemId = await runQuickCreate136({
        user,
        name,
        curatedPlaceOfSafety,
      });
      navigate(createRoute.incidentProgressPage(incidentWorkItemId));
    },
    [navigate, user],
  );

  if (!isQuickCreateEnabled) {
    return null;
  }

  return (
    <QuickCreateDemoIncidentDialog
      open={isOpen}
      onClose={close}
      onCreate={create}
    />
  );
}
